import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

interface OnlineUser {
  userId: string;
  userType: string;
  nome: string;
  empresaId?: string;
  ultimaAtualizacao?: Timestamp;
  conectadoEm?: Timestamp;
  online: boolean;
}

const CentralHome: React.FC = () => {
  const { userData, logout, userId } = useAuth();
  const navigate = useNavigate();
  const [usuariosOnline, setUsuariosOnline] = useState<OnlineUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'Empresa' | 'Motoboy' | 'Central'>('todos');

  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }

    const usuariosPresence: OnlineUser[] = [];
    const motoboysAceitando: OnlineUser[] = [];

    // Função para atualizar a lista combinada
    const atualizarLista = () => {
      // Combinar listas, priorizando presence para motoboys
      const usuariosUnicos = new Map<string, OnlineUser>();
      
      // Primeiro adicionar motoboys que estão aceitando pedidos
      motoboysAceitando.forEach(m => {
        usuariosUnicos.set(m.userId, m);
      });
      
      // Depois adicionar usuários da presence (sobrescreve motoboys se existir)
      usuariosPresence.forEach(u => {
        usuariosUnicos.set(u.userId, u);
      });

      const listaFinal = Array.from(usuariosUnicos.values());

      // Ordenar por tipo e nome
      listaFinal.sort((a, b) => {
        if (a.userType !== b.userType) {
          const ordem = { 'Central': 0, 'Empresa': 1, 'Motoboy': 2 };
          return (ordem[a.userType as keyof typeof ordem] || 99) - (ordem[b.userType as keyof typeof ordem] || 99);
        }
        return a.nome.localeCompare(b.nome);
      });

      setUsuariosOnline(listaFinal);
      setIsLoading(false);
    };

    // Buscar usuários online da coleção presence
    const presenceRef = collection(db, 'presence');
    const q = query(presenceRef, where('online', '==', true));

    const unsubscribePresence = onSnapshot(q, (snapshot) => {
      usuariosPresence.length = 0; // Limpar array
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as OnlineUser;
        
        // Verificar se a última atualização foi há menos de 1 minuto
        if (data.ultimaAtualizacao) {
          const ultimaAtualizacao = data.ultimaAtualizacao.toDate();
          const agora = new Date();
          const diferenca = agora.getTime() - ultimaAtualizacao.getTime();
          
          // Considerar online apenas se atualizou há menos de 1 minuto
          if (diferenca < 60000) {
            usuariosPresence.push(data);
          }
        } else if (data.online) {
          usuariosPresence.push(data);
        }
      });

      atualizarLista();
    }, (error) => {
      console.error('Erro ao carregar usuários online:', error);
      setIsLoading(false);
    });

    // Buscar motoboys online da coleção usuarios (aceitandoPedidos === true)
    const usuariosRef = collection(db, 'usuarios');
    const qMotoboys = query(
      usuariosRef,
      where('tipo', '==', 'Motoboy'),
      where('aceitandoPedidos', '==', true)
    );

    const unsubscribeMotoboys = onSnapshot(qMotoboys, (motoboysSnapshot) => {
      motoboysAceitando.length = 0; // Limpar array
      
      motoboysSnapshot.docs.forEach((doc) => {
        const motoboyData = doc.data();
        
        motoboysAceitando.push({
          userId: doc.id,
          userType: 'Motoboy',
          nome: motoboyData.nome || 'Motoboy',
          online: true,
          ultimaAtualizacao: undefined
        });
      });

      atualizarLista();
    }, (error) => {
      console.error('Erro ao carregar motoboys online:', error);
    });

    return () => {
      unsubscribePresence();
      unsubscribeMotoboys();
    };
  }, [userId, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const usuariosFiltrados = filtroTipo === 'todos' 
    ? usuariosOnline 
    : usuariosOnline.filter(u => u.userType === filtroTipo);

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'Central':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Empresa':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Motoboy':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'Central':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      case 'Empresa':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'Motoboy':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Painel Central</h1>
              <p className="text-sm text-gray-500">Rota Entregas - Sistema de Gestão</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Online</span>
              </div>
              <span className="text-gray-700 font-medium">Olá, {userData?.nome || 'Central'}</span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors shadow-lg hover:shadow-xl"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Usuários Online</h2>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-gray-700">
                {usuariosFiltrados.length} usuário(s) online agora
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setFiltroTipo('todos')}
              className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                filtroTipo === 'todos'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos ({usuariosOnline.length})
            </button>
            <button
              onClick={() => setFiltroTipo('Central')}
              className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                filtroTipo === 'Central'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Central ({usuariosOnline.filter(u => u.userType === 'Central').length})
            </button>
            <button
              onClick={() => setFiltroTipo('Empresa')}
              className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                filtroTipo === 'Empresa'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Empresas ({usuariosOnline.filter(u => u.userType === 'Empresa').length})
            </button>
            <button
              onClick={() => setFiltroTipo('Motoboy')}
              className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                filtroTipo === 'Motoboy'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Motoboys ({usuariosOnline.filter(u => u.userType === 'Motoboy').length})
            </button>
          </div>
        </div>

        {/* Lista de Usuários */}
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhum usuário online
            </h3>
            <p className="text-gray-500">
              {filtroTipo === 'todos' 
                ? 'Não há usuários online no momento' 
                : `Não há usuários do tipo "${filtroTipo}" online no momento`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usuariosFiltrados.map((usuario) => (
              <div
                key={usuario.userId}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getTipoColor(usuario.userType)}`}>
                      {getTipoIcon(usuario.userType)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{usuario.nome}</h3>
                      <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium border ${getTipoColor(usuario.userType)}`}>
                        {usuario.userType}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mb-1"></div>
                    <span className="text-xs text-gray-500">Online</span>
                  </div>
                </div>

                {usuario.ultimaAtualizacao && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        Ativo há {format(usuario.ultimaAtualizacao.toDate(), "HH:mm", { locale: ptBR })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CentralHome;

